import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, MessageCircle } from "lucide-react";
import { useUnread } from "../lib/community";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "../lib/auth";

export const BrandLogo = ({ className = "" }) => (
  <span className={`flex items-center gap-2 md:gap-2.5 ${className}`}>
    <img src="/brand/logo-mark.png" alt="" className="h-9 md:h-11 w-auto" draggable="false" />
    <img src="/brand/logo-wordmark.png" alt="LockedInForever" className="h-[14px] md:h-[18px] w-auto" draggable="false" />
  </span>
);

const NAV = [
  {
    label: "Features",
    children: [
      { label: "Digital Invites & RSVP", to: "/#features" },
      { label: "Guest List Manager", to: "/#features" },
      { label: "Memory Vault", to: "/#features" },
      { label: "Wedding Timeline", to: "/#features" },
      { label: "Seating Chart", to: "/#features" },
      { label: "AI Vow Studio", to: "/dashboard/vow-studio" },
    ],
  },
  {
    label: "Couple Websites",
    children: [
      { label: "Build Your Site", to: "/signup" },
      { label: "Templates", to: "/#features" },
      { label: "Social (stories & reels)", to: "/social" },
      { label: "Browse Wedding Feed", to: "/feed" },
      { label: "Hall of Fame", to: "/featured" },
      { label: "Couples Club", to: "/club" },
    ],
  },
  {
    label: "Who It's For",
    children: [
      { label: "For Couples", to: "/for-couples" },
      { label: "For Planners", to: "/for-planners" },
      { label: "Find a Planner", to: "/planners" },
      { label: "For Venues", to: "/for-venues" },
      { label: "Social Center", to: "/hub" },
      { label: "Discover Vendors", to: "/hub?tab=vendors" },
      { label: "Community", to: "/community" },
      { label: "Become a Vendor", to: "/dashboard/vendor-profile" },
    ],
  },
  { label: "Consignment", to: "/market" },
  { label: "Social Center", to: "/hub" },
  { label: "Pricing", to: "/pricing" },
  { label: "Podcast", to: "/podcast" },
  {
    label: "Resources",
    children: [
      { label: "Live Broadcasts", to: "/live" },
      { label: "Refer & Earn", to: "/dashboard/referrals" },
      { label: "Careers – Work From Home", to: "/careers" },
    ],
  },
];

// overlay: transparent over a hero until scrolled; tone "light" = white text for dark heroes.
export default function Navbar({ overlay = false, tone = "light" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const unread = useUnread(user);

  useEffect(() => {
    if (!overlay) return;
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [overlay]);

  const clear = overlay && !scrolled && !open;
  const light = clear && tone === "light";
  const ink = light ? "text-white" : "text-[var(--ink)]";

  function handleLink(to) {
    if (to.startsWith("/#")) {
      const id = to.split("#")[1];
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          const el = document.getElementById(id);
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
    }
    setOpen(false);
    setOpenDropdown(null);
  }

  return (
    <header
      data-testid="site-navbar"
      data-state={clear ? "clear" : "solid"}
      className={`sticky top-0 left-0 right-0 z-40 h-16 md:h-20 transition-[background-color,box-shadow,border-color] duration-500 border-b ${overlay ? "-mb-16 md:-mb-20" : ""} ${clear ? "bg-transparent border-transparent" : "bg-[#FAF7F2]/95 backdrop-blur-xl border-[var(--line)] shadow-[0_8px_30px_-12px_rgba(17,17,17,0.18)]"}`}
    >
      {clear && <div className="absolute inset-0 -z-10 bg-gradient-to-b from-black/45 to-transparent pointer-events-none" style={{ opacity: light ? 1 : 0 }} />}
      <div className="container-wide h-full flex items-center justify-between gap-3 px-4 md:px-12">
        <Link to="/" data-testid="logo-link" className="shrink-0 transition-transform duration-300 hover:scale-[1.02]">
          <BrandLogo />
        </Link>

        <nav className="hidden xl:flex items-center gap-6 2xl:gap-8">
          {NAV.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => item.children && setOpenDropdown(item.label)}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <button
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => item.to && handleLink(item.to)}
                className={`whitespace-nowrap text-[11.5px] uppercase tracking-[0.16em] ${ink} hover:text-[var(--gold)] transition-colors flex items-center gap-1`}
              >
                {item.label}
                {item.children && <ChevronDown className="w-3 h-3" />}
              </button>
              {item.children && openDropdown === item.label && (
                <div className="absolute top-full left-0 pt-2 z-50">
                  <div className="bg-white border border-[var(--line)] shadow-lg rounded-xl py-2 min-w-[220px]">
                    {item.children.map((c) => (
                      <button
                        key={c.label}
                        onClick={() => handleLink(c.to)}
                        data-testid={`nav-child-${c.to.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "") || "home"}`}
                        className="w-full text-left text-[13px] px-4 py-2 hover:bg-[var(--bg-warm)] text-[var(--ink-soft)] hover:text-[var(--gold)]"
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3 shrink-0 whitespace-nowrap">
          {user && (
            <Link to="/messages" data-testid="nav-messages" aria-label="Messages" className={`hidden md:flex relative w-10 h-10 rounded-full items-center justify-center hover:text-[var(--gold)] transition-colors ${ink}`}>
              <MessageCircle className="w-5 h-5" />
              {unread > 0 && <span data-testid="nav-messages-badge" className="absolute top-0.5 right-0 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] leading-4 text-center">{unread > 9 ? "9+" : unread}</span>}
            </Link>
          )}
          {user ? (
            <Link to={user.account_type === "seller" && !user.plan_selected ? "/market/sell" : user.role === "guest" ? "/hub" : "/dashboard"} data-testid="dashboard-link" className="btn-gold !px-4 !py-2 !text-[11px] md:!px-7 md:!py-3">
              {user.role === "guest" ? "Social Center" : "Dashboard"}
            </Link>
          ) : (
            <>
              <Link to="/login" data-testid="login-link" className={`!hidden md:!inline-flex btn-outline-gold !px-6 !py-3 ${light ? "!text-white !border-white/50 hover:!bg-white hover:!text-black" : ""}`}>
                Log In
              </Link>
              <Link to="/signup" data-testid="get-started-btn" className="btn-gold !px-4 !py-2 !text-[11px] md:!px-7 md:!py-3">
                Get Started
              </Link>
            </>
          )}

          <button
            data-testid="mobile-menu-btn"
            className={`xl:hidden w-10 h-10 rounded-full flex items-center justify-center active:bg-black/10 transition-colors ${ink}`}
            onClick={() => setOpen((o) => !o)}
            aria-label="Open menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
      {open && (
        <motion.div key="mobile-menu" data-testid="mobile-menu" initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.22 }}
          className="xl:hidden absolute inset-x-0 top-full bg-white border-t border-[var(--line)] shadow-xl rounded-b-3xl p-6 flex flex-col gap-4 z-50 max-h-[75vh] overflow-y-auto">
          {NAV.map((item) => (
            <div key={item.label}>
              <button
                onClick={() => item.to ? handleLink(item.to) : setOpenDropdown(openDropdown === item.label ? null : item.label)}
                className="text-sm tracking-wide text-[var(--ink)] uppercase tracking-[0.18em] flex items-center gap-2"
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {item.label}
                {item.children && <ChevronDown className="w-3 h-3" />}
              </button>
              {item.children && openDropdown === item.label && (
                <div className="pl-4 mt-2 flex flex-col gap-2">
                  {item.children.map((c) => (
                    <button key={c.label} onClick={() => handleLink(c.to)} className="text-left text-sm text-[var(--muted)]" data-testid={`nav-mobile-child-${c.to.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "") || "home"}`}>{c.label}</button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {!user && <Link to="/login" onClick={() => setOpen(false)} data-testid="mobile-menu-login" className="btn-outline-gold mt-2">Log In</Link>}
        </motion.div>
      )}
      </AnimatePresence>
    </header>
  );
}
